import { DEFAULT_ROLE_PERMISSIONS, type UserRole } from '@utazzo/shared';

import { canAccessNavItem } from './nav-access';
import { NAV_GROUPS, type NavItemConfig } from './nav-config';

import type { WebsitePermission } from '@/features/website-cms/lib';

/**
 * Which product this build is. The same admin codebase ships as two images on
 * two domains:
 *   - `crm`      -> utazzocrm.com: the full CRM, with the invoicing/accounts
 *                   surface removed entirely.
 *   - `services` -> utazzoservices.com: the accounts app, scoped to what the
 *                   FINANCE role can access (invoicing + the masters FINANCE is
 *                   granted). Managed from the CRM /roles screen, so new FINANCE
 *                   grants show up here automatically.
 * Read straight off `import.meta.env` (a Vite build-time literal) rather than
 * the validated `env` object, so these checks constant-fold and the unused
 * product's code (e.g. the whole invoicing surface on the CRM build) tree-shakes
 * out of each bundle. `env.ts` still validates VITE_APP_VARIANT at boot.
 */
export type AppVariant = 'crm' | 'services';
export const IS_SERVICES = import.meta.env.VITE_APP_VARIANT === 'services';
export const APP_VARIANT: AppVariant = IS_SERVICES ? 'services' : 'crm';
export const IS_CRM = APP_VARIANT === 'crm';

/** The nav groups that make up the invoicing / accounts (Services) product. */
const SERVICES_GROUP_IDS = new Set(['finance', 'financial-reports']);

/** Ids of every nav item inside the invoicing / accounts groups. */
const FINANCE_ITEM_IDS = new Set(
  NAV_GROUPS.filter((g) => SERVICES_GROUP_IDS.has(g.id)).flatMap((g) => g.items.map((i) => i.id)),
);

/**
 * Items that sit in a CRM nav group but belong to the accounts team, so they
 * show ONLY on the Services app. 2026-10-20: supplier fund requests raised by
 * operations are paid from utazzoservices.com, not the CRM.
 */
export const SERVICES_ONLY_ITEM_IDS: ReadonlySet<string> = new Set(['fund-requests']);

/**
 * FINANCE's seed permission set, used to cap the Services surface for NON-finance
 * users (admins) so they see the same accounts-only app. A FINANCE user is capped
 * by their own live `/me` set instead, so a grant added from the CRM /roles
 * screen reflects immediately for the accounts team.
 */
const FINANCE_DEFAULT_PERMS = new Set<string>(DEFAULT_ROLE_PERMISSIONS.finance ?? []);

/** Roles allowed into the Services (accounts) app at all. */
export const SERVICES_ALLOWED_ROLES: readonly UserRole[] = ['ADMIN', 'FINANCE'];

export const isServicesAllowedRole = (role: UserRole | null | undefined): boolean =>
  role != null && SERVICES_ALLOWED_ROLES.includes(role);

/**
 * Variant-aware nav visibility. Used by the sidebar, command palette and quick
 * links so all three agree on what this product shows.
 *
 * - CRM: the invoicing/accounts items are hidden entirely (they live only on the
 *   Services app); everything else uses the normal access check.
 * - Services: every item is evaluated AS the FINANCE role, so admins see the same
 *   accounts-only surface and any module FINANCE is granted appears here.
 */
export function canSeeNavItem(
  item: NavItemConfig,
  role: UserRole | null,
  can: (action: WebsitePermission) => boolean,
  effectivePermissions: Set<string> | null | undefined,
  email?: string | null,
  assignedRoleCodes?: readonly string[] | null,
): boolean {
  if (IS_SERVICES) {
    const financePerms = role === 'FINANCE' ? effectivePermissions : FINANCE_DEFAULT_PERMS;
    // ADMIN-only items in the finance groups (e.g. supplier-change approvals) must see the real role,
    // otherwise the FINANCE evaluation below would hide them from everyone.
    const adminOnly =
      FINANCE_ITEM_IDS.has(item.id) &&
      item.requireRole?.length === 1 &&
      item.requireRole[0] === 'ADMIN';
    return canAccessNavItem(
      item,
      adminOnly ? role : 'FINANCE',
      can,
      financePerms,
      email,
      assignedRoleCodes,
    );
  }
  if (FINANCE_ITEM_IDS.has(item.id) || SERVICES_ONLY_ITEM_IDS.has(item.id)) return false;
  return canAccessNavItem(item, role, can, effectivePermissions, email, assignedRoleCodes);
}
